import envConfig from '@/lib/env-config';
import { integrationDataService } from '@/lib/integration-data-service';
import type { RootState } from '@/store';
import { type IntegrationEndpoint } from '@/store/slices/integrationSlice';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAuthRedux } from './use-auth-redux';

export interface UseIntegrationReturn {
  partner: any;
  endpoints: IntegrationEndpoint[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  getEndpointUrl: (
    action: string,
    environment?: 'development' | 'staging' | 'production'
  ) => Promise<{ success: boolean; url?: string; error?: string }>;
}

/**
 * Custom hook for accessing integration partner data
 * Uses centralized IntegrationDataService to prevent duplicate API calls
 */
export const useIntegration = (): UseIntegrationReturn => {
  const { user } = useAuthRedux();
  const { partner, endpoints, dataFetched } = useSelector(
    (state: RootState) => state.integration
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchIntegrationData = async () => {
    if (!user?.ev_integration_partner_id) {
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await integrationDataService.fetchIntegrationDataIfNeeded(
        user.ev_integration_partner_id
      );

      if (result.success) {
        setError(null);
      } else {
        setError(result.error || 'Failed to fetch integration data');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Unexpected error';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getEndpointUrl = async (
    action: string,
    environment: string = envConfig.env
  ) => {
    if (!user?.ev_integration_partner_id) {
      return {
        success: false,
        error: 'User not associated with integration partner',
      };
    }

    // Use the centralized service to get endpoint URL
    const endpoint = endpoints.find(
      (ep) => ep.action === action && ep.environment === environment
    );

    if (!endpoint) {
      return {
        success: false,
        error: `Endpoint not found for action: ${action} and environment: ${environment}`,
      };
    }

    return {
      success: true,
      url: endpoint.endpoint_url,
    };
  };

  // Fetch integration data when user is authenticated and data is not fetched
  useEffect(() => {
    if (user?.ev_integration_partner_id && !dataFetched) {
      fetchIntegrationData();
    } else if (user?.ev_integration_partner_id && dataFetched) {
    } else {
    }
  }, [user?.ev_integration_partner_id, dataFetched]);

  return {
    partner: partner,
    endpoints: endpoints,
    loading,
    error,
    refetch: fetchIntegrationData,
    getEndpointUrl,
  };
};
